import {defineMessages} from 'react-intl';
import type {IntlShape} from 'react-intl';
import type {
  UserFacingErrorCode,
  ErrorCode,
  ErrorContext,
} from 'src/v2/state/types';
import {getStore} from 'src/v2/state/store';
import {ApolloError} from '@sail/data';
import {absurd} from 'src/utils/absurd';
import {logErrorToSentry} from 'src/v2/util/sentryLogger';
import {getLogger} from 'src/utils/logger';
import {isMobile} from 'src/utils/utils';

type CustomDisplayErrorName = 'gasless_fallback_error';

export class CustomDisplayError extends Error {
  override name: CustomDisplayErrorName;

  constructor(name: CustomDisplayErrorName) {
    super(name);
    this.name = name;
  }
}

export const clearErrorBanner = (): void => {
  const store = getStore();
  store.setValue((state) => {
    state.errorBanner = null;
  });
};

// Check for wallet prompt user cancellation messages (based on useEvmSendTransactionDynamic.ts)
const WALLET_PROMPT_USER_CANCELLATION_ERRORS = [
  'user denied transaction signature',
  'user rejected the request',
  'user has rejected the request',
  'user canceled',
  'cancelled by user',
  'user denied',
  'user rejected',
];

// Check for QR code expiration messages
export const QR_CODE_EXPIRATION_ERROR = 'proposal expired';

const messages = defineMessages<UserFacingErrorCode>({
  transaction_error: {
    id: 'crypto.payins.v2.error.transaction_error',
    description:
      'Error message when an error occurs during processing a transaction',
    defaultMessage:
      'We couldn’t complete your transaction. Try again or contact support.',
  },
  wallet_connection_error: {
    id: 'crypto.payins.v2.error.wallet_connection_unknown_error',
    description:
      'Error message when an unknown error occurs during wallet connection',
    defaultMessage:
      'We couldn’t connect to your wallet. Try again or contact support.',
  },
  unknown_error: {
    id: 'crypto.payins.v2.error.unknown_error',
    description: 'Error message when an unknown error occurs',
    defaultMessage: 'An unknown error occurred.',
  },
  exchange_payment_error: {
    id: 'crypto.payins.v2.error.exchange_payment_error',
    description: 'Error message when an error occurs during exchange payment',
    defaultMessage:
      'Payment with Crypto.com is currently unavailable. Please try again later or connect another wallet.',
  },
  refund_address_error: {
    id: 'crypto.payins.v2.error.refund_address_error',
    description:
      'Error message when an error occurs while saving refund address',
    defaultMessage:
      "We couldn't save your refund address. Please check the address and try again.",
  },
  gasless_fallback_error: {
    id: 'crypto.payins.v2.error.gasless_fallback',
    description:
      'Error message when gasless payment fails, prompting retry via standard payment',
    defaultMessage:
      "We currently don't support gasless payments with smart wallets. Please try again with a standard payment, or switch to an EOA wallet.",
  },
});

export const handleError = ({
  error,
  intl,
  context,
}: {
  error: unknown;
  intl: IntlShape;
  context: ErrorContext;
}): void => {
  const store = getStore();
  const logger = getLogger();
  let errorCode: ErrorCode = 'unknown_error';
  let displayMessage: React.ReactNode;

  if (error instanceof ApolloError) {
    errorCode = 'transaction_error';
    // We render the error message from the API instead of a generic error message.
    // TODO: gather api errors from we want to translate from https://stripe.sourcegraphcloud.com/stripe-internal/pay-server/-/blob/lib/api_errors/messages/en_US.yaml?L31504
    displayMessage = error.message;
  } else if (error instanceof CustomDisplayError) {
    errorCode = error.name;
    displayMessage = intl.formatMessage(messages[error.name]);
  } else if (error instanceof Error) {
    const lowerMessage = error.message.toLowerCase();

    const isUserCancellation = WALLET_PROMPT_USER_CANCELLATION_ERRORS.some(
      (msg) => lowerMessage.includes(msg),
    );

    const isExchangePaymentError = lowerMessage.includes(
      'exchange_payment_error',
    );

    if (isExchangePaymentError) {
      errorCode = 'exchange_payment_error';
      displayMessage = intl.formatMessage(messages.exchange_payment_error);
    } else if (isUserCancellation) {
      // Render error message based on screen/context
      switch (context) {
        case 'wallet_connection':
          errorCode = 'wallet_connection_canceled';
          break;
        case 'transfer':
          errorCode = 'transfer_canceled';
          break;
        case 'setup':
          errorCode = 'setup_canceled';
          break;
        case 'network_switch':
          errorCode = 'network_switch_canceled';
          break;
        case 'exchange_payment':
        case 'direct_deposit':
        case 'unknown':
          // This shouldn't happen - all wallet prompt cancellations should be handled in a valid context.
          errorCode = 'unknown_error';
          break;
        default:
          absurd(context);
          break;
      }
    } else if (isMobile() && lowerMessage.includes('missing public address')) {
      // Suppress this error - return early without setting error banner until Dynamic fixed the issue
      logger.log('crypto_payin_session.error', {
        error_code: 'missing_public_address',
      });
      return;
    }
    // All other errors remain as 'unknown'
    else {
      switch (context) {
        case 'wallet_connection':
          errorCode = 'wallet_connection_error';
          break;
        case 'transfer':
        case 'setup':
          errorCode = 'transaction_error';
          break;
        case 'direct_deposit':
          errorCode = 'refund_address_error';
          break;
        default:
          errorCode = 'unknown_error';
          break;
      }
      displayMessage = intl.formatMessage(messages[errorCode]);
    }
  } else {
    // Handle non-Error objectss
    errorCode = 'unknown_error';
    displayMessage = intl.formatMessage(messages[errorCode]);
  }

  logger.log('crypto_payin_session.error', {
    error_code: errorCode,
    error_context: context,
    error_message: error instanceof Error ? error.message : String(error),
  });

  // TODO: We may stop logging known/user-cancellation errors to Sentry in the future.
  // Log everything for now for easy testing.
  logErrorToSentry(error, context, errorCode);

  // Set the error banner in the store
  store.setValue((state) => {
    state.overlay = null;
    if (displayMessage) {
      state.errorBanner = {
        code: errorCode,
        message:
          typeof displayMessage === 'string'
            ? displayMessage
            : intl.formatMessage(messages.unknown_error),
      };
    }
  });
};
